import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Plus, Trash2, ChevronDown, ChevronRight, Pencil, History } from "lucide-react";
import { formatCurrency } from "@/components/StatusBadge";
import { useAuth, hasRole } from "@/contexts/AuthContext";
import { useTableSort } from "@/lib/useTableSort";
import { BarChart, Bar, XAxis, YAxis, Tooltip as RTooltip, ResponsiveContainer, Cell } from "recharts";

const BAR_COLORS = ["#FF7F00", "#10B981", "#3B82F6", "#8B5CF6", "#EC4899", "#F59E0B", "#14B8A6", "#EF4444"];

function BdIncentiveChart({ totals, selected, onBarClick }) {
  if (!totals || totals.length === 0) return null;
  const data = totals
    .map((t) => ({ name: t.bd_name || t.bd_email || "—", earned: t.total_earned_inr || 0, pending: t.pending_inr || 0 }))
    .sort((a, b) => b.earned - a.earned)
    .slice(0, 10);
  return (
    <div className="rounded-lg border border-slate-200 p-4 mt-4" data-testid="incentive-chart">
      <div className="text-sm font-heading font-medium text-slate-800 mb-1">Top BDs by Earned Incentive (INR)</div>
      <div className="text-xs text-slate-500 mb-3">Click a bar to filter the table below.</div>
      <div style={{ width: "100%", height: 260 }}>
        <ResponsiveContainer>
          <BarChart data={data} margin={{ top: 12, right: 16, left: 0, bottom: 40 }}>
            <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} angle={-25} textAnchor="end" />
            <YAxis tick={{ fontSize: 11 }} />
            <RTooltip formatter={(v) => new Intl.NumberFormat("en-IN").format(Math.round(v))} />
            <Bar dataKey="earned" radius={[4, 4, 0, 0]} onClick={(d) => onBarClick?.(d?.name)} className="cursor-pointer">
              {data.map((d, i) => (
                <Cell key={i}
                      fill={BAR_COLORS[i % BAR_COLORS.length]}
                      fillOpacity={selected && selected !== d.name ? 0.35 : 1} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      {selected && (
        <button type="button" onClick={() => onBarClick?.(null)}
                className="text-xs text-slate-500 underline hover:text-slate-700 mt-2"
                data-testid="incentive-clear-filter">
          Clear filter ({selected})
        </button>
      )}
    </div>
  );
}

function BreakdownRow({ item }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <tr className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => setOpen(!open)}>
        <td className="py-2 px-3 flex items-center gap-1 text-sm">
          {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
          <span className="font-medium">{item.bd_name}</span>
        </td>
        <td className="py-2 px-3 text-sm">{item.period_month}</td>
        <td className="py-2 px-3 text-sm text-emerald-700">{formatCurrency(item.earned_inr, "INR")}</td>
        <td className="py-2 px-3 text-sm text-slate-700">{formatCurrency(item.paid_inr, "INR")}</td>
        <td className="py-2 px-3 text-sm font-medium text-amber-700">{formatCurrency(item.balance_inr, "INR")}</td>
      </tr>
      {open && (
        <tr>
          <td colSpan={5} className="bg-slate-50 p-0">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-slate-500">
                  <th className="text-left py-2 px-4">Date</th>
                  <th className="text-left py-2 px-4">Client</th>
                  <th className="text-left py-2 px-4">Project</th>
                  <th className="text-left py-2 px-4">Received</th>
                  <th className="text-left py-2 px-4">INR</th>
                  <th className="text-left py-2 px-4">Seq. (tier)</th>
                  <th className="text-left py-2 px-4">%</th>
                  <th className="text-right py-2 px-4">Incentive INR</th>
                </tr>
              </thead>
              <tbody>
                {item.breakdown.map((b, i) => {
                  const upper = (b.applied_tier_upper === null || b.applied_tier_upper === undefined) ? "+" : b.applied_tier_upper;
                  const band = (b.applied_tier_lower ? (upper === "+" ? `${b.applied_tier_lower}+` : `${b.applied_tier_lower}–${upper}`) : `≤${b.threshold ?? "?"}`);
                  return (
                  <tr key={b.payment_id || `${b.payment_date}|${b.project_code}|${i}`} className="border-t border-slate-200">
                    <td className="py-2 px-4">{b.payment_date?.slice(0, 10)}</td>
                    <td className="py-2 px-4">{b.firm_name}</td>
                    <td className="py-2 px-4"><span className="font-mono">{b.project_code}</span> — {b.project_name}</td>
                    <td className="py-2 px-4">{formatCurrency(b.amount_received, b.currency)}</td>
                    <td className="py-2 px-4">{formatCurrency(b.amount_received_inr, "INR")}</td>
                    <td className="py-2 px-4">#{b.project_sequence_for_client} <span className="text-slate-400">({band})</span></td>
                    <td className="py-2 px-4">{b.applied_percentage}%</td>
                    <td className="py-2 px-4 text-right font-medium text-emerald-700">{formatCurrency(b.incentive_inr, "INR")}</td>
                  </tr>
                );})}
              </tbody>
            </table>
          </td>
        </tr>
      )}
    </>
  );
}

function ReassignBdPanel({ users, onDone }) {
  const [fromBd, setFromBd] = useState("");
  const [toBd, setToBd] = useState("");
  const [effectiveDate, setEffectiveDate] = useState(new Date().toISOString().slice(0, 10));
  const [clients, setClients] = useState([]);           // clients of departing BD
  const [selected, setSelected] = useState(new Set());
  const [selectAll, setSelectAll] = useState(true);
  // Inherited tier ladder — same shape as the main rule form. Defaults to a
  // conservative 2-tier ladder (matches the previous 1% / 0.5% behavior) so the
  // panel opens with a sensible starting point.
  const [tiers, setTiers] = useState([
    { up_to_projects: 5, percentage: 1 },
    { up_to_projects: "", percentage: 0.5 },
  ]);
  const [scope, setScope] = useState("per_client");
  const [closeOldRules, setCloseOldRules] = useState(true);
  const [transferActiveOnly, setTransferActiveOnly] = useState(true);
  const [notes, setNotes] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!fromBd) { setClients([]); setSelected(new Set()); setPreview(null); return; }
    // Departing BD's clients
    api.get(`/api/clients?_bd=${fromBd}`).then(async () => {
      const r = await api.get("/api/clients");
      const owned = (r.data || []).filter((c) => c.business_development_owner_id === fromBd);
      setClients(owned);
      setSelected(new Set(owned.map((c) => c.id)));
    });
  }, [fromBd]);

  const toggleClient = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next); setSelectAll(next.size === clients.length);
  };
  const toggleAll = () => {
    if (selectAll) { setSelected(new Set()); setSelectAll(false); }
    else { setSelected(new Set(clients.map((c) => c.id))); setSelectAll(true); }
  };

  const addTier = () => {
    setTiers((list) => {
      const copy = [...list];
      const lastIdx = copy.length - 1;
      const lastIsOpen = copy.length && (copy[lastIdx].up_to_projects === "" || copy[lastIdx].up_to_projects === null);
      const prevUp = lastIsOpen && lastIdx >= 1 ? Number(copy[lastIdx - 1].up_to_projects || 0) : Number(copy[lastIdx]?.up_to_projects || 0);
      const newTier = { up_to_projects: prevUp + 5, percentage: 0 };
      if (lastIsOpen) copy.splice(lastIdx, 0, newTier);
      else copy.push(newTier);
      return copy;
    });
  };
  const removeTier = (idx) => {
    setTiers((list) => (list.length <= 1 ? list : list.filter((_, i) => i !== idx)));
  };
  const setTierField = (idx, key, value) => {
    setTiers((list) => list.map((t, i) => (i === idx ? { ...t, [key]: value } : t)));
  };

  // Normalize the tier ladder and validate it locally (mirrors backend rules).
  const normalizeTiers = () => {
    const n = tiers.map((t) => ({
      up_to_projects: t.up_to_projects === "" || t.up_to_projects === null || t.up_to_projects === undefined
        ? null : Number(t.up_to_projects),
      percentage: Number(t.percentage || 0),
    }));
    let prev = 0;
    for (let i = 0; i < n.length; i++) {
      const t = n[i];
      if (t.percentage < 0) return { error: `Tier ${i + 1}: percentage must be >= 0` };
      if (t.up_to_projects === null) {
        if (i !== n.length - 1) return { error: "Only the LAST tier may leave 'up to' blank." };
      } else {
        if (t.up_to_projects <= prev) return { error: `Tier ${i + 1}: 'up to' must be greater than ${prev}.` };
        prev = t.up_to_projects;
      }
    }
    return { tiers: n };
  };

  const build = () => {
    const norm = normalizeTiers();
    if (norm.error) return { _localError: norm.error };
    const n = norm.tiers;
    return {
      from_bd_id: fromBd, to_bd_id: toBd,
      effective_date: effectiveDate,
      client_ids: selectAll ? null : Array.from(selected),
      transfer_active_projects_only: transferActiveOnly,
      // New multi-tier fields
      inherited_tiers: n,
      inherited_scope: scope,
      // Legacy fields still populated for backward compat (mirror first/last band)
      inherited_threshold_projects: n[0].up_to_projects || 0,
      inherited_primary_percentage: n[0].percentage,
      inherited_secondary_percentage: n[n.length - 1].percentage,
      close_departing_rules: closeOldRules,
      notes: notes || null,
    };
  };

  const runPreview = async () => {
    if (!fromBd || !toBd) return toast.error("Choose both departing and target BDs");
    if (fromBd === toBd) return toast.error("Departing and target BDs must differ");
    if (selected.size === 0 && !selectAll) return toast.error("Pick at least one client to transfer");
    const body = build();
    if (body._localError) return toast.error(body._localError);
    setBusy(true);
    try {
      const { data } = await api.post("/api/incentives/reassign/preview", body);
      setPreview(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Preview failed"); }
    finally { setBusy(false); }
  };
  const runCommit = async () => {
    if (!preview) return toast.error("Run Preview first to review the change");
    if (!window.confirm(`Confirm: move ${preview.clients_count} client(s) and ${preview.projects_count} project(s) from ${preview.from_bd.name} → ${preview.to_bd.name}?`)) return;
    const body = build();
    if (body._localError) return toast.error(body._localError);
    setBusy(true);
    try {
      const { data } = await api.post("/api/incentives/reassign", body);
      toast.success(data.message);
      setPreview(null); setFromBd(""); setToBd(""); setSelected(new Set());
      onDone && onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Reassignment failed"); }
    finally { setBusy(false); }
  };

  const bdOptions = users;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-4 mt-4" data-testid="reassign-panel">
      {/* Left column — form */}
      <Card className="border-slate-200 shadow-none p-5 space-y-4">
        <div>
          <h3 className="font-heading text-lg font-semibold">Bulk BD Reassignment</h3>
          <p className="text-xs text-slate-500 mt-1">
            Move a departing BD's clients + active projects to a new owner in one step. <b>History stays untouched</b> — every past receipt already recorded under the departing BD keeps counting for them. The new BD earns the (usually lower) inherited rate on transferred clients from the effective date onward.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>Departing BD *</Label>
            <Select value={fromBd} onValueChange={setFromBd}>
              <SelectTrigger data-testid="reassign-from-bd"><SelectValue placeholder="Select departing BD" /></SelectTrigger>
              <SelectContent>{bdOptions.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} · {u.email}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Target BD (new owner) *</Label>
            <Select value={toBd} onValueChange={setToBd}>
              <SelectTrigger data-testid="reassign-to-bd"><SelectValue placeholder="Select new BD" /></SelectTrigger>
              <SelectContent>{bdOptions.filter((u) => u.id !== fromBd).map((u) => <SelectItem key={u.id} value={u.id}>{u.name} · {u.email}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Effective Date *</Label>
            <Input type="date" value={effectiveDate} onChange={(e) => setEffectiveDate(e.target.value)} data-testid="reassign-date" />
            <div className="text-[11px] text-slate-500">New terms apply from this date. Historic receipts stay attributed to {bdOptions.find((u) => u.id === fromBd)?.name || "the departing BD"}.</div>
          </div>
          <div className="space-y-1.5">
            <Label>Transfer scope</Label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={transferActiveOnly} onChange={(e) => setTransferActiveOnly(e.target.checked)} />
              Active + in-progress projects only (recommended)
            </label>
          </div>
        </div>

        {/* Client picker */}
        {fromBd && (
          <div className="border-t border-slate-100 pt-3">
            <div className="flex items-center justify-between mb-2">
              <Label>Clients to transfer ({selected.size} of {clients.length})</Label>
              <button type="button" className="text-xs text-slate-500 hover:text-slate-800 underline" onClick={toggleAll} data-testid="reassign-toggle-all">
                {selectAll ? "Deselect all" : "Select all"}
              </button>
            </div>
            <div className="max-h-56 overflow-y-auto border border-slate-200 rounded-md">
              {clients.length === 0 ? (
                <div className="text-center text-slate-500 text-sm py-6">This BD has no active clients.</div>
              ) : clients.map((c) => (
                <label key={c.id} className="flex items-center gap-2 px-3 py-1.5 border-b border-slate-100 last:border-b-0 hover:bg-slate-50">
                  <input type="checkbox"
                         checked={selected.has(c.id)}
                         onChange={() => toggleClient(c.id)}
                         data-testid={`reassign-client-${c.id}`} />
                  <span className="text-sm">{c.firm_name}</span>
                  {c.status && <span className="ml-auto text-[10px] text-slate-400 uppercase">{c.status}</span>}
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Inherited rate */}
        <div className="border-t border-slate-100 pt-3">
          <Label>Inherited incentive rate for the new BD on these clients</Label>
          <div className="grid grid-cols-1 gap-2 mt-2">
            <div className="space-y-1.5">
              <div className="text-[11px] text-slate-500">Count scope</div>
              <Select value={scope} onValueChange={setScope}>
                <SelectTrigger data-testid="reassign-scope"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="per_client">Per client — Nth project of each inherited client</SelectItem>
                  <SelectItem value="per_bd_all_clients">Across all clients — Nth project the new BD closes overall</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between mt-1">
              <div className="text-[11px] text-slate-500">Tiered percentages</div>
              <Button type="button" variant="outline" size="sm" onClick={addTier} data-testid="reassign-add-tier">
                <Plus className="w-3 h-3 mr-1" />Add tier
              </Button>
            </div>
            <div className="border border-slate-200 rounded-md divide-y divide-slate-100">
              {tiers.map((t, i) => {
                const isLast = i === tiers.length - 1;
                const lower = i === 0 ? 1 : (Number(tiers[i - 1].up_to_projects) || 0) + 1;
                const openBand = t.up_to_projects === "" || t.up_to_projects === null || t.up_to_projects === undefined;
                return (
                  <div key={`re-tier-${i}`} className="flex items-center gap-2 p-2" data-testid={`reassign-tier-row-${i}`}>
                    <span className="text-xs text-slate-500 w-16 shrink-0">Tier {i + 1}</span>
                    <div className="flex items-center gap-1 text-sm">
                      <span className="text-slate-500">projects</span>
                      <span className="font-mono">{lower}</span>
                      <span className="text-slate-500">to</span>
                      <Input
                        type="number"
                        className="w-20 h-8"
                        placeholder={isLast ? "∞" : ""}
                        value={t.up_to_projects ?? ""}
                        onChange={(e) => setTierField(i, "up_to_projects", e.target.value)}
                        data-testid={`reassign-tier-upto-${i}`}
                      />
                      {isLast && openBand && <span className="text-slate-500 text-xs">(and above)</span>}
                    </div>
                    <div className="flex items-center gap-1 text-sm ml-auto">
                      <span className="text-slate-500">@</span>
                      <Input
                        type="number"
                        step="0.01"
                        className="w-20 h-8"
                        value={t.percentage}
                        onChange={(e) => setTierField(i, "percentage", e.target.value)}
                        data-testid={`reassign-tier-pct-${i}`}
                      />
                      <span className="text-slate-500">%</span>
                    </div>
                    <button
                      type="button"
                      className="text-slate-400 hover:text-rose-600 disabled:opacity-30"
                      disabled={tiers.length <= 1}
                      onClick={() => removeTier(i)}
                      data-testid={`reassign-tier-remove-${i}`}
                      title="Remove tier"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="text-[11px] text-slate-500 mt-2">
            Kept intentionally low — the new BD didn't build these client relationships. Their regular rule on their own clients is untouched. Ranges are inclusive-lower / inclusive-upper (e.g. 1–5, 6–10, 11+); leave the LAST tier's "up to" blank to mean "and above".
          </div>
        </div>

        <div className="border-t border-slate-100 pt-3 space-y-2">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={closeOldRules} onChange={(e) => setCloseOldRules(e.target.checked)} />
            Also cap the departing BD's open rules on {effectiveDate} <span className="text-slate-400">(prevents any future payment counting for them)</span>
          </label>
          <div className="space-y-1.5">
            <Label>Notes (optional)</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Reason for reassignment, hand-over context, etc." />
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={runPreview} disabled={busy || !fromBd || !toBd} data-testid="reassign-preview-btn">Preview</Button>
          <Button className="bg-brand hover:bg-brand-hover" onClick={runCommit} disabled={busy || !preview} data-testid="reassign-commit-btn">Confirm & Reassign</Button>
        </div>
      </Card>

      {/* Right column — preview */}
      <Card className="border-slate-200 shadow-none p-5">
        <h3 className="font-heading text-lg font-semibold">Preview</h3>
        {!preview ? (
          <p className="text-slate-500 text-sm mt-2">Fill in the form on the left and click Preview to see exactly what will change.</p>
        ) : (
          <div className="mt-3 space-y-3 text-sm">
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-emerald-800 text-xs">
              🔒 <b>{preview.from_bd.name}</b> keeps every past receipt ({formatCurrency(preview.past_incentive_inr_departing_bd || 0, "INR")} in earned incentives so far). Historic dashboards remain intact.
            </div>
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div className="rounded border border-slate-200 p-2">
                <div className="uppercase text-[10px] text-slate-500">Clients moving</div>
                <div className="font-heading text-2xl mt-1">{preview.clients_count}</div>
              </div>
              <div className="rounded border border-slate-200 p-2">
                <div className="uppercase text-[10px] text-slate-500">Active projects moving</div>
                <div className="font-heading text-2xl mt-1">{preview.projects_count}</div>
              </div>
            </div>
            <div className="rounded border border-slate-200 p-2 text-xs">
              <div className="uppercase text-[10px] text-slate-500 mb-1">Departing BD's open rules will be capped</div>
              {(preview.open_rules_that_will_close || []).length === 0 ? (
                <span className="text-slate-500">No open rules to cap.</span>
              ) : preview.open_rules_that_will_close.map((r) => {
                const rTiers = (r.tiers && r.tiers.length) ? r.tiers : [
                  { up_to_projects: null, percentage: r.primary_percentage },
                  { up_to_projects: null, percentage: r.secondary_percentage },
                ];
                const label = r.tiers && r.tiers.length
                  ? r.tiers.map((t, i) => {
                      const lower = i === 0 ? 1 : (Number(r.tiers[i - 1].up_to_projects) || 0) + 1;
                      const upper = (t.up_to_projects === null || t.up_to_projects === undefined) ? "+" : t.up_to_projects;
                      return upper === "+" ? `${lower}+ @ ${t.percentage}%` : `${lower}–${upper} @ ${t.percentage}%`;
                    }).join(" · ")
                  : `Primary ${r.primary_percentage}% / Secondary ${r.secondary_percentage}%`;
                return (
                  <div key={r.id}>{label} (from {r.effective_from?.slice(0, 10)} → will end on {preview.effective_date})</div>
                );
              })}
            </div>
            <div className="rounded border border-sky-200 bg-sky-50 p-2 text-xs">
              <div className="uppercase text-[10px] text-sky-700 mb-1">New inherited rule for {preview.to_bd.name}</div>
              <div>Scope: <b>{(preview.new_inherited_rule.scope || "per_client") === "per_bd_all_clients" ? "All clients" : "Per client"}</b></div>
              {preview.new_inherited_rule.tiers && preview.new_inherited_rule.tiers.length ? (
                <div className="mt-1">
                  {preview.new_inherited_rule.tiers.map((t, i, arr) => {
                    const lower = i === 0 ? 1 : (Number(arr[i - 1].up_to_projects) || 0) + 1;
                    const upper = (t.up_to_projects === null || t.up_to_projects === undefined) ? "+" : t.up_to_projects;
                    const band = upper === "+" ? `${lower}+` : `${lower}–${upper}`;
                    return (
                      <div key={`prev-tier-${i}`}>Tier {i + 1}: <b>{band}</b> projects @ <b className="text-emerald-800">{t.percentage}%</b></div>
                    );
                  })}
                </div>
              ) : (
                <>
                  <div>Threshold: <b>{preview.new_inherited_rule.threshold_projects}</b> projects/client</div>
                  <div>Primary: <b className="text-emerald-800">{preview.new_inherited_rule.primary_percentage}%</b> · Secondary: <b className="text-emerald-800">{preview.new_inherited_rule.secondary_percentage}%</b></div>
                </>
              )}
              <div>Effective from: <b>{preview.new_inherited_rule.effective_from}</b></div>
              <div className="mt-1 text-slate-500">Applies ONLY to the {preview.clients_count} transferred client(s). Their regular rule stays as-is on other clients.</div>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}


export default function Incentives() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const canManagePayouts = hasRole(user, "admin", "accounts");
  const [tab, setTab] = useState("summary");
  const [users, setUsers] = useState([]);
  const [rules, setRules] = useState([]);
  const [summary, setSummary] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [totals, setTotals] = useState([]);
  const [selectedBd, setSelectedBd] = useState(null); // chart drill-through
  const [monthFilter, setMonthFilter] = useState("");
  const [openRule, setOpenRule] = useState(false);
  const [openPayout, setOpenPayout] = useState(false);
  const [editingPayout, setEditingPayout] = useState(null);
  const [historyPayout, setHistoryPayout] = useState(null);
  const [payoutHistory, setPayoutHistory] = useState(null);

  const emptyRule = {
    bd_user_id: "",
    scope: "per_client",
    tiers: [
      { up_to_projects: 5, percentage: 5 },
      { up_to_projects: "", percentage: 1 },
    ],
    effective_from: "",
    effective_to: "",
    notes: "",
  };
  const [rForm, setRForm] = useState(emptyRule);
  const [editingRuleId, setEditingRuleId] = useState(null);
  const emptyPayout = { bd_user_id: "", period_month: new Date().toISOString().slice(0, 7), amount_inr: 0, paid_date: new Date().toISOString().slice(0, 10), reference: "", notes: "" };
  const [pForm, setPForm] = useState(emptyPayout);
  const [clientsMap, setClientsMap] = useState({});

  const load = async () => {
    const params = monthFilter ? `?period_month=${monthFilter}` : "";
    const [s, r, p, u, t, cRes] = await Promise.all([
      api.get(`/api/incentives/summary${params}`),
      api.get("/api/incentives/rules").catch(() => ({ data: [] })),
      api.get("/api/incentives/payouts").catch(() => ({ data: [] })),
      canManagePayouts ? api.get("/api/users") : Promise.resolve({ data: [] }),
      api.get("/api/incentives/totals").catch(() => ({ data: [] })),
      isAdmin ? api.get("/api/clients").catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
    ]);
    setSummary(s.data); setRules(r.data); setPayouts(p.data); setTotals(t.data);
    setUsers((u.data || []).filter((x) => x.role === "business_development"));
    if (isAdmin) {
      const map = {};
      (cRes.data || []).forEach((c) => { map[c.id] = c.firm_name; });
      setClientsMap(map);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [monthFilter]);

  const saveRule = async () => {
    if (!rForm.bd_user_id) return toast.error("Select a BD user");
    // Normalize the tier ladder: coerce numbers, treat empty string as null (∞)
    const tiers = (rForm.tiers || []).map((t) => ({
      up_to_projects: t.up_to_projects === "" || t.up_to_projects === null || t.up_to_projects === undefined
        ? null
        : Number(t.up_to_projects),
      percentage: Number(t.percentage || 0),
    }));
    if (!tiers.length) return toast.error("Add at least one tier");
    // Local sanity check mirroring the backend
    let prev = 0;
    for (let i = 0; i < tiers.length; i++) {
      const t = tiers[i];
      if (t.percentage < 0) return toast.error(`Tier ${i + 1}: percentage must be >= 0`);
      if (t.up_to_projects === null) {
        if (i !== tiers.length - 1) return toast.error("Only the LAST tier may leave 'up to' blank.");
      } else {
        if (t.up_to_projects <= prev) return toast.error(`Tier ${i + 1}: 'up to' must be greater than ${prev}.`);
        prev = t.up_to_projects;
      }
    }
    try {
      const body = {
        bd_user_id: rForm.bd_user_id,
        scope: rForm.scope || "per_client",
        tiers,
        // Legacy fields kept for backward-compat consumers — always mirror
        // the first (primary) and last (secondary) bands of the ladder.
        threshold_projects: tiers[0].up_to_projects || 0,
        primary_percentage: tiers[0].percentage,
        secondary_percentage: tiers[tiers.length - 1].percentage,
        effective_from: rForm.effective_from || null,
        effective_to: rForm.effective_to || null,
        notes: rForm.notes || null,
      };
      if (editingRuleId) {
        // PATCH doesn't accept bd_user_id changes
        const { bd_user_id: _bd, ...patchBody } = body;
        await api.patch(`/api/incentives/rules/${editingRuleId}`, patchBody);
        toast.success("Rule updated");
      } else {
        await api.post("/api/incentives/rules", body);
        toast.success("Rule saved");
      }
      setOpenRule(false); setEditingRuleId(null); setRForm(emptyRule); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const openEditRule = (r) => {
    const tiers = (r.tiers && r.tiers.length) ? r.tiers.map((t) => ({
      up_to_projects: t.up_to_projects === null || t.up_to_projects === undefined ? "" : t.up_to_projects,
      percentage: t.percentage,
    })) : [
      { up_to_projects: r.threshold_projects ?? 5, percentage: r.primary_percentage ?? 0 },
      { up_to_projects: "", percentage: r.secondary_percentage ?? 0 },
    ];
    setEditingRuleId(r.id);
    setRForm({
      bd_user_id: r.bd_user_id || "",
      scope: r.scope || "per_client",
      tiers,
      effective_from: r.effective_from?.slice(0, 10) || "",
      effective_to: r.effective_to?.slice(0, 10) || "",
      notes: r.notes || "",
    });
    setOpenRule(true);
  };

  const addTier = () => {
    setRForm((f) => {
      const list = [...(f.tiers || [])];
      // Insert BEFORE the last "∞" tier if present, else append.
      const lastIdx = list.length - 1;
      const lastIsOpen = list.length && (list[lastIdx].up_to_projects === "" || list[lastIdx].up_to_projects === null);
      const prevUp = lastIsOpen && lastIdx >= 1 ? Number(list[lastIdx - 1].up_to_projects || 0) : Number(list[lastIdx]?.up_to_projects || 0);
      const newTier = { up_to_projects: prevUp + 5, percentage: 0 };
      if (lastIsOpen) list.splice(lastIdx, 0, newTier);
      else list.push(newTier);
      return { ...f, tiers: list };
    });
  };
  const removeTier = (idx) => {
    setRForm((f) => {
      const list = [...(f.tiers || [])];
      if (list.length <= 1) return f;
      list.splice(idx, 1);
      return { ...f, tiers: list };
    });
  };
  const setTierField = (idx, key, value) => {
    setRForm((f) => {
      const list = [...(f.tiers || [])];
      list[idx] = { ...list[idx], [key]: value };
      return { ...f, tiers: list };
    });
  };

  const deleteRule = async (id) => {
    if (!window.confirm("Delete this rule?")) return;
    await api.delete(`/api/incentives/rules/${id}`); load();
  };

  const savePayout = async () => {
    if (!pForm.bd_user_id) return toast.error("Select a BD user");
    if (Number(pForm.amount_inr) <= 0) return toast.error("Amount must be > 0");
    try {
      await api.post("/api/incentives/payouts", { ...pForm, amount_inr: Number(pForm.amount_inr) });
      toast.success("Payout recorded"); setOpenPayout(false); setPForm(emptyPayout); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  // ------ Edit / delete a recorded payout (admin + accounts only) ------
  const openEditPayout = (p) => {
    setEditingPayout(p);
    setPForm({
      bd_user_id: p.bd_user_id,
      period_month: p.period_month || "",
      amount_inr: p.amount_inr || 0,
      paid_date: p.paid_date?.slice(0, 10) || "",
      reference: p.reference || "",
      notes: p.notes || "",
    });
  };
  const saveEditedPayout = async () => {
    if (Number(pForm.amount_inr) <= 0) return toast.error("Amount must be > 0");
    try {
      await api.patch(`/api/incentives/payouts/${editingPayout.id}`, {
        period_month: pForm.period_month || null,
        amount_inr: Number(pForm.amount_inr),
        paid_date: pForm.paid_date || null,
        reference: pForm.reference || null,
        notes: pForm.notes || null,
      });
      toast.success("Payout updated — every change is recorded in the audit log");
      setEditingPayout(null); setPForm(emptyPayout); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const deletePayoutRow = async (p) => {
    if (!window.confirm(`Delete payout of ₹${p.amount_inr.toLocaleString()} to ${p.bd_name || "BD"}?`)) return;
    try {
      await api.delete(`/api/incentives/payouts/${p.id}`);
      toast.success("Payout deleted");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const openHistoryDialog = async (p) => {
    setHistoryPayout(p); setPayoutHistory(null);
    try {
      const { data } = await api.get(`/api/incentives/payouts/${p.id}/history`);
      setPayoutHistory(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const rulesColumns = [
    { key: "bd", label: "BD", getValue: (r) => r.bd_name || "" },
    { key: "scope", label: "Scope", getValue: (r) => ((r.scope || "per_client") === "per_bd_all_clients" ? "All clients" : "Per client") },
    { key: "primary_pct", label: "Tiers", numeric: true, getValue: (r) => {
        const tiers = (r.tiers && r.tiers.length) ? r.tiers : [{ percentage: r.primary_percentage }];
        return Number(tiers[0]?.percentage || 0);
      } },
    { key: "applies_to", label: "Applies to", numeric: true, getValue: (r) => (Array.isArray(r.applies_to_client_ids) ? r.applies_to_client_ids.length : 0) },
    { key: "effective_from", label: "Effective", getValue: (r) => r.effective_from || "" },
  ];
  const { sortedRows: sortedRules, header: rulesHeader } = useTableSort(rules, rulesColumns, "effective_from", "desc");

  const payoutsColumns = [
    { key: "paid_date", label: "Paid Date", getValue: (p) => p.paid_date || "" },
    { key: "bd", label: "BD", getValue: (p) => p.bd_name || "" },
    { key: "period", label: "Period", getValue: (p) => p.period_month || "" },
    { key: "amount", label: "Amount INR", numeric: true, align: "right", getValue: (p) => p.amount_inr || 0 },
    { key: "reference", label: "Reference", getValue: (p) => p.reference || "" },
    { key: "notes", label: "Notes", getValue: (p) => p.notes || "" },
  ];
  const { sortedRows: sortedPayouts, header: payoutsHeader } = useTableSort(payouts, payoutsColumns, "paid_date", "desc", { trailingActionColumn: canManagePayouts });

  const summaryColumns = [
    { key: "bd", label: "BD", getValue: (s) => s.bd_name || "" },
    { key: "period", label: "Period", getValue: (s) => s.period_month || "" },
    { key: "earned", label: "Earned", numeric: true, getValue: (s) => s.earned_inr || 0 },
    { key: "paid", label: "Paid", numeric: true, getValue: (s) => s.paid_inr || 0 },
    { key: "balance", label: "Balance", numeric: true, getValue: (s) => s.balance_inr || 0 },
  ];
  const { sortedRows: sortedSummary, header: summaryHeader } = useTableSort(summary, summaryColumns, "period", "desc", { trailingActionColumn: false });

  return (
    <div className="space-y-6" data-testid="incentives-page">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-heading font-semibold tracking-tight">BD Incentives</h1>
          <p className="text-sm text-slate-500 mt-1">Per-BD monthly incentive tracking (based on actual INR received).</p>
        </div>
        <div className="flex items-center gap-2">
          {canManagePayouts && (
            <Dialog open={openPayout} onOpenChange={setOpenPayout}>
              <DialogTrigger asChild><Button variant="outline" data-testid="record-payout-btn"><Plus className="w-4 h-4 mr-1" />Record Payout</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Record Incentive Payout</DialogTitle></DialogHeader>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2 space-y-1.5"><Label>BD *</Label>
                    <Select value={pForm.bd_user_id} onValueChange={(v) => setPForm({ ...pForm, bd_user_id: v })}>
                      <SelectTrigger data-testid="payout-bd"><SelectValue placeholder="Select BD" /></SelectTrigger>
                      <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} · {u.email}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5"><Label>Period (YYYY-MM)</Label><Input value={pForm.period_month} onChange={(e) => setPForm({ ...pForm, period_month: e.target.value })} placeholder="2026-02" /></div>
                  <div className="space-y-1.5"><Label>Amount (INR) *</Label><Input type="number" data-testid="payout-amount" value={pForm.amount_inr} onChange={(e) => setPForm({ ...pForm, amount_inr: e.target.value })} /></div>
                  <div className="space-y-1.5"><Label>Paid Date</Label><Input type="date" value={pForm.paid_date} onChange={(e) => setPForm({ ...pForm, paid_date: e.target.value })} /></div>
                  <div className="space-y-1.5"><Label>Reference</Label><Input value={pForm.reference} onChange={(e) => setPForm({ ...pForm, reference: e.target.value })} /></div>
                  <div className="col-span-2 space-y-1.5"><Label>Notes</Label><Textarea value={pForm.notes} onChange={(e) => setPForm({ ...pForm, notes: e.target.value })} /></div>
                </div>
                <DialogFooter><Button variant="outline" onClick={() => setOpenPayout(false)}>Cancel</Button><Button className="bg-brand hover:bg-brand-hover" onClick={savePayout} data-testid="save-payout-btn">Save</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          )}
          {isAdmin && (
            <Dialog open={openRule} onOpenChange={(v) => { setOpenRule(v); if (!v) { setEditingRuleId(null); setRForm(emptyRule); } }}>
              <DialogTrigger asChild><Button className="bg-brand hover:bg-brand-hover" data-testid="add-rule-btn" onClick={() => { setEditingRuleId(null); setRForm(emptyRule); }}><Plus className="w-4 h-4 mr-1" />New Rule</Button></DialogTrigger>
              <DialogContent className="max-h-[90vh] flex flex-col p-0 gap-0">
                <DialogHeader className="px-6 pt-6 pb-3 border-b border-slate-100 shrink-0">
                  <DialogTitle>{editingRuleId ? "Edit Incentive Rule" : "New Incentive Rule"}</DialogTitle>
                </DialogHeader>
                <div className="flex-1 overflow-y-auto px-6 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2 space-y-1.5"><Label>BD *</Label>
                    <Select value={rForm.bd_user_id} onValueChange={(v) => setRForm({ ...rForm, bd_user_id: v })} disabled={!!editingRuleId}>
                      <SelectTrigger data-testid="rule-bd"><SelectValue placeholder="Select BD" /></SelectTrigger>
                      <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} · {u.email}</SelectItem>)}</SelectContent>
                    </Select>
                    {editingRuleId && <div className="text-[11px] text-slate-500">BD cannot be changed on an existing rule — delete and create a new one to move it to a different BD.</div>}
                  </div>
                  <div className="col-span-2 space-y-1.5"><Label>Count scope</Label>
                    <Select value={rForm.scope || "per_client"} onValueChange={(v) => setRForm({ ...rForm, scope: v })}>
                      <SelectTrigger data-testid="rule-scope"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="per_client">Per client — Nth project of each client</SelectItem>
                        <SelectItem value="per_bd_all_clients">Across all clients — Nth project the BD closed overall</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2 space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>Tiered percentages</Label>
                      <Button type="button" variant="outline" size="sm" onClick={addTier} data-testid="add-tier-btn">
                        <Plus className="w-3 h-3 mr-1" />Add tier
                      </Button>
                    </div>
                    <div className="border border-slate-200 rounded-md divide-y divide-slate-100">
                      {(rForm.tiers || []).map((t, i) => {
                        const isLast = i === (rForm.tiers?.length || 0) - 1;
                        const lower = i === 0 ? 1 : (Number(rForm.tiers[i - 1].up_to_projects) || 0) + 1;
                        const openBand = t.up_to_projects === "" || t.up_to_projects === null || t.up_to_projects === undefined;
                        return (
                          <div key={`tier-${i}`} className="flex items-center gap-2 p-2" data-testid={`tier-row-${i}`}>
                            <span className="text-xs text-slate-500 w-20 shrink-0">Tier {i + 1}</span>
                            <div className="flex items-center gap-1 text-sm">
                              <span className="text-slate-500">projects</span>
                              <span className="font-mono">{lower}</span>
                              <span className="text-slate-500">to</span>
                              <Input
                                type="number"
                                className="w-20 h-8"
                                placeholder={isLast ? "∞" : ""}
                                value={t.up_to_projects ?? ""}
                                onChange={(e) => setTierField(i, "up_to_projects", e.target.value)}
                                data-testid={`tier-upto-${i}`}
                              />
                              {isLast && openBand && <span className="text-slate-500 text-xs">(and above)</span>}
                            </div>
                            <div className="flex items-center gap-1 text-sm ml-auto">
                              <span className="text-slate-500">@</span>
                              <Input
                                type="number"
                                step="0.01"
                                className="w-20 h-8"
                                value={t.percentage}
                                onChange={(e) => setTierField(i, "percentage", e.target.value)}
                                data-testid={`tier-pct-${i}`}
                              />
                              <span className="text-slate-500">%</span>
                            </div>
                            <button
                              type="button"
                              className="text-slate-400 hover:text-rose-600 disabled:opacity-30"
                              disabled={(rForm.tiers?.length || 0) <= 1}
                              onClick={() => removeTier(i)}
                              data-testid={`tier-remove-${i}`}
                              title="Remove tier"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                    <div className="text-xs text-slate-500">
                      Leave the LAST tier's "up to" blank to mean "and above". Ranges are inclusive-lower / inclusive-upper (e.g. 1–5, 6–10, 11+).
                    </div>
                  </div>
                  <div className="space-y-1.5"><Label>Effective From</Label><Input type="date" value={rForm.effective_from} onChange={(e) => setRForm({ ...rForm, effective_from: e.target.value })} /></div>
                  <div className="space-y-1.5"><Label>Effective To <span className="text-slate-400 text-xs">(blank = ongoing)</span></Label><Input type="date" value={rForm.effective_to} onChange={(e) => setRForm({ ...rForm, effective_to: e.target.value })} /></div>
                  <div className="col-span-2 space-y-1.5"><Label>Notes</Label><Textarea value={rForm.notes} onChange={(e) => setRForm({ ...rForm, notes: e.target.value })} /></div>
                </div>
                <div className="text-xs text-slate-500 border-t border-slate-100 mt-4 pt-3">
                  Example: <em>1–5 @ 8%, 6–10 @ 5%, 11+ @ 2%</em> — the BD earns 8% of INR received on their first 5 projects for a client, 5% on the next 5, and 2% on every project after that.
                </div>
                </div>
                <DialogFooter className="px-6 py-4 border-t border-slate-100 shrink-0 bg-white">
                  <Button variant="outline" onClick={() => setOpenRule(false)}>Cancel</Button>
                  <Button className="bg-brand hover:bg-brand-hover" onClick={saveRule} data-testid="save-rule-btn">{editingRuleId ? "Update Rule" : "Save Rule"}</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="summary">Monthly Summary</TabsTrigger>
          {isAdmin && <TabsTrigger value="rules" data-testid="rules-tab">Rules</TabsTrigger>}
          <TabsTrigger value="payouts">Payouts</TabsTrigger>
          {isAdmin && <TabsTrigger value="reassign" data-testid="reassign-tab">Reassign BD</TabsTrigger>}
        </TabsList>

        <TabsContent value="summary">
          <BdIncentiveChart
            totals={totals}
            selected={selectedBd}
            onBarClick={(name) => setSelectedBd((prev) => prev === name ? null : name)}
          />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            {totals.map((t) => (
              <Card key={t.bd_user_id} className="border-slate-200 shadow-none p-5">
                <div className="text-xs uppercase tracking-wider text-slate-500">{t.bd_name || t.bd_email}</div>
                <div className="mt-3 grid grid-cols-3 gap-3">
                  <div>
                    <div className="text-[10px] uppercase text-slate-500">Earned</div>
                    <div className="font-heading text-lg font-semibold text-emerald-700">{formatCurrency(t.total_earned_inr, "INR")}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase text-slate-500">Paid</div>
                    <div className="font-heading text-lg font-semibold text-slate-700">{formatCurrency(t.total_paid_inr, "INR")}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase text-slate-500">Pending</div>
                    <div className="font-heading text-lg font-semibold text-amber-700">{formatCurrency(t.pending_inr, "INR")}</div>
                  </div>
                </div>
              </Card>
            ))}
            {totals.length === 0 && (
              <Card className="border-slate-200 shadow-none p-6 md:col-span-3 text-slate-500 text-sm text-center">
                No incentive activity yet. Add a rule and record a payment with Actual INR to see totals here.
              </Card>
            )}
          </div>
          <Card className="border-slate-200 shadow-none p-4 mt-4">
            <div className="flex items-center gap-2 mb-3">
              <Label className="text-xs">Filter by month:</Label>
              <Input className="w-40" placeholder="YYYY-MM" value={monthFilter} onChange={(e) => setMonthFilter(e.target.value)} data-testid="month-filter" />
            </div>
            <table className="w-full">
              <thead className="text-xs uppercase text-slate-500 border-b border-slate-200">
                {summaryHeader}
              </thead>
              <tbody>
                {sortedSummary.map((s) => <BreakdownRow key={`${s.bd_id || s.bd_name}|${s.period_month || s.period || ""}`} item={s} />)}
                {sortedSummary.length === 0 && <tr><td colSpan={5} className="text-center text-slate-500 py-8">No incentive earnings yet. Add a rule for a BD, then record a payment against a project they own.</td></tr>}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        {isAdmin && (
        <TabsContent value="rules">
          <Card className="border-slate-200 shadow-none p-4 mt-4">
            <table className="data-table w-full">
              <thead>{rulesHeader}</thead>
              <tbody>
                {sortedRules.map((r) => {
                  const tiers = (r.tiers && r.tiers.length) ? r.tiers : [
                    { up_to_projects: r.threshold_projects, percentage: r.primary_percentage },
                    { up_to_projects: null, percentage: r.secondary_percentage },
                  ];
                  const label = tiers.map((t, i) => {
                    const lower = i === 0 ? 1 : (Number(tiers[i - 1].up_to_projects) || 0) + 1;
                    const upper = (t.up_to_projects === null || t.up_to_projects === undefined) ? "+" : t.up_to_projects;
                    return upper === "+" ? `${lower}+ @ ${t.percentage}%` : `${lower}–${upper} @ ${t.percentage}%`;
                  }).join(" · ");
                  const scoped = Array.isArray(r.applies_to_client_ids) && r.applies_to_client_ids.length > 0;
                  const clientNames = scoped ? r.applies_to_client_ids.map((cid) => clientsMap[cid] || cid.slice(0, 6)) : [];
                  return (
                    <tr key={r.id}>
                      <td className="font-medium">{r.bd_name} <span className="text-slate-400">· {r.bd_email}</span></td>
                      <td className="text-xs text-slate-600">
                        {(r.scope || "per_client") === "per_bd_all_clients" ? "All clients" : "Per client"}
                      </td>
                      <td className="text-sm">{label}</td>
                      <td className="text-xs">
                        {scoped ? (
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-brand-tint text-brand-dark border border-brand/30"
                            title={clientNames.join(", ")}
                            data-testid={`rule-applies-to-${r.id}`}
                          >
                            {clientNames.length === 1 ? clientNames[0] : `${clientNames.length} clients`}
                          </span>
                        ) : (
                          <span className="text-slate-400">All of this BD's clients</span>
                        )}
                      </td>
                      <td className="text-slate-500 text-xs">
                        {r.effective_from?.slice(0, 10) || "—"} → {r.effective_to?.slice(0, 10) || "ongoing"}
                      </td>
                      <td>{isAdmin && (
                        <div className="flex items-center gap-2">
                          <button className="text-slate-400 hover:text-brand" onClick={() => openEditRule(r)} data-testid={`edit-rule-${r.id}`} title="Edit rule"><Pencil className="w-4 h-4" /></button>
                          <button className="text-slate-400 hover:text-rose-600" onClick={() => deleteRule(r.id)} data-testid={`delete-rule-${r.id}`} title="Delete rule"><Trash2 className="w-4 h-4" /></button>
                        </div>
                      )}</td>
                    </tr>
                  );
                })}
                {sortedRules.length === 0 && <tr><td colSpan={6} className="text-center text-slate-500 py-8">No rules configured yet.</td></tr>}
              </tbody>
            </table>
          </Card>
        </TabsContent>
        )}

        <TabsContent value="payouts">
          <Card className="border-slate-200 shadow-none p-4 mt-4">
            <div className="text-xs text-slate-500 mb-2">
              {canManagePayouts
                ? "Every past payout is editable — every change is captured in the audit log. BDs see this page in read-only mode."
                : "Read-only view of your paid incentives. Contact accounts if a number needs correcting."}
            </div>
            <table className="data-table w-full">
              <thead>{payoutsHeader}</thead>
              <tbody>
                {sortedPayouts.map((p) => (
                  <tr key={p.id} data-testid={`payout-row-${p.id}`}>
                    <td>{p.paid_date?.slice(0, 10)}</td>
                    <td className="font-medium">{p.bd_name}</td>
                    <td>{p.period_month}</td>
                    <td className="text-right text-emerald-700 font-medium tabular-nums">{formatCurrency(p.amount_inr, "INR")}</td>
                    <td className="text-slate-500">{p.reference || "—"}</td>
                    <td className="text-slate-500 text-xs max-w-[240px] truncate" title={p.notes || ""}>{p.notes || "—"}</td>
                    {canManagePayouts && (
                      <td>
                        <div className="flex gap-1 justify-end">
                          <button title="View history" className="p-1.5 rounded hover:bg-slate-100 text-slate-500" onClick={() => openHistoryDialog(p)} data-testid={`payout-history-${p.id}`}>
                            <History className="w-3.5 h-3.5" />
                          </button>
                          <button title="Edit payout" className="p-1.5 rounded hover:bg-slate-100 text-slate-500" onClick={() => openEditPayout(p)} data-testid={`payout-edit-${p.id}`}>
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                          <button title="Delete payout" className="p-1.5 rounded hover:bg-rose-50 text-rose-500" onClick={() => deletePayoutRow(p)} data-testid={`payout-delete-${p.id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
                {sortedPayouts.length === 0 && <tr><td colSpan={canManagePayouts ? 7 : 6} className="text-center text-slate-500 py-8">No payouts recorded yet.</td></tr>}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        {isAdmin && (
          <TabsContent value="reassign">
            <ReassignBdPanel users={users} onDone={load} />
          </TabsContent>
        )}
      </Tabs>

      {/* Edit payout dialog */}
      <Dialog open={!!editingPayout} onOpenChange={(v) => { if (!v) { setEditingPayout(null); setPForm(emptyPayout); } }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit Payout · {editingPayout?.bd_name}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 text-xs text-slate-500 bg-amber-50 border border-amber-200 rounded px-3 py-2">
              Correcting a payout is audited — every field change is stored as a before → after snapshot. BDs cannot see or reach this dialog.
            </div>
            <div className="space-y-1.5"><Label>Period (YYYY-MM)</Label><Input value={pForm.period_month} onChange={(e) => setPForm({ ...pForm, period_month: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Amount (INR) *</Label><Input type="number" data-testid="edit-payout-amount" value={pForm.amount_inr} onChange={(e) => setPForm({ ...pForm, amount_inr: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Paid Date</Label><Input type="date" value={pForm.paid_date} onChange={(e) => setPForm({ ...pForm, paid_date: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Reference</Label><Input value={pForm.reference} onChange={(e) => setPForm({ ...pForm, reference: e.target.value })} /></div>
            <div className="col-span-2 space-y-1.5"><Label>Notes</Label><Textarea value={pForm.notes} onChange={(e) => setPForm({ ...pForm, notes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setEditingPayout(null); setPForm(emptyPayout); }}>Cancel</Button>
            <Button className="bg-brand hover:bg-brand-hover" onClick={saveEditedPayout} data-testid="save-edit-payout">Save changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payout history dialog */}
      <Dialog open={!!historyPayout} onOpenChange={(v) => { if (!v) { setHistoryPayout(null); setPayoutHistory(null); } }}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Payout History · {historyPayout?.bd_name}</DialogTitle></DialogHeader>
          {!payoutHistory ? (
            <div className="text-slate-500 text-center py-6">Loading…</div>
          ) : payoutHistory.history.length === 0 ? (
            <div className="text-slate-500 text-center py-6">No changes recorded — this payout has never been edited.</div>
          ) : (
            <ol className="relative border-l-2 border-slate-200 ml-2 space-y-4 pl-4 pt-2">
              {payoutHistory.history.map((h, idx) => (
                <li key={h.at || `hist-${idx}`} className="relative" data-testid={`payout-history-event-${idx}`}>
                  <span className={`absolute -left-[22px] top-1.5 w-3 h-3 rounded-full ${
                    h.action === "create" ? "bg-emerald-500" :
                    h.action === "edit" ? "bg-amber-500" :
                    h.action === "delete" ? "bg-rose-500" : "bg-slate-300"
                  } border-2 border-white`}></span>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="text-sm font-medium capitalize">Payout {h.action}</div>
                      {h.action === "edit" && (
                        <div className="text-xs text-slate-500 mt-0.5 space-y-0.5">
                          {(() => {
                            const before = h.changes?.before || {};
                            const after = h.changes?.after || {};
                            const keys = Array.from(new Set([...Object.keys(before), ...Object.keys(after)]));
                            return keys.map((k) => (
                              <div key={k}>
                                <b className="text-slate-700">{k.replace(/_/g, " ")}</b>:{" "}
                                <span className="line-through text-slate-400">{String(before[k] ?? "—")}</span>
                                <span className="mx-1">→</span>
                                <span className="text-slate-800 font-medium">{String(after[k] ?? "—")}</span>
                              </div>
                            ));
                          })()}
                        </div>
                      )}
                      {h.action === "create" && (
                        <div className="text-xs text-slate-500 mt-0.5">
                          Amount ₹{h.changes?.amount_inr?.toLocaleString?.() || h.changes?.amount_inr} · Period {h.changes?.period_month || "—"}
                        </div>
                      )}
                      {h.action === "delete" && (
                        <div className="text-xs text-rose-600 mt-0.5">
                          Payout removed. Historic amount ₹{h.changes?.amount_inr?.toLocaleString?.()}.
                        </div>
                      )}
                    </div>
                    <div className="text-[11px] text-slate-400 whitespace-nowrap text-right">
                      {h.created_at && new Date(h.created_at).toLocaleString()}
                      {h.actor_name && <div>by {h.actor_name}</div>}
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
